<template>
  <div class="page-wrapper">
    <NavInit />

    <!-- ===== HERO TEXT ===== -->
    <div class="hero-text-section">
      <div class="space-y-10">
        <div class="space-y-2">
          <p class="text-4xl lg:text-7xl font-rockinsoda text-navy">HANGA</p>
          <p class="text-4xl lg:text-7xl font-rockinsoda text-navy">Sexual &amp; Reproductive</p>
          <p class="text-4xl lg:text-7xl font-rockinsoda text-navy">Health (SRH) PROGRAM</p>
        </div>
        <div class="text-justify mt-4 lg:mt-0 w-full lg:w-2/3">
          <p class="text-base lg:text-2xl font-gramatika text-navy">
            Exploring opportunities that exist for tech-enabled startups to solve SRH issues in Sub Saharan Africa.
          </p>
        </div>
      </div>
    </div>

    <!-- ===== HERO IMAGE ===== -->
    <div class="relative w-full flex-shrink-0 mt-12 p-4 lg:p-10">
      <img src="../assets/new-1.jpg" class="w-full h-[40vh] lg:h-[120vh] object-cover" alt="Hanga SRH" />
    </div>

    <!-- ===== ABOUT SECTION ===== -->
    <div class="about-section">
      <!-- Left: Title -->
      <div class="lg:text-left w-full lg:w-2/4 space-y-6 mt-8 lg:mt-20">
        <p class="text-3xl lg:text-6xl font-rockinsoda text-navy">About Hanga SRH</p>
      </div>

      <!-- Right: Sliding text -->
      <div class="mt-4 lg:mt-0 w-full lg:w-2/4 flex flex-col items-start" ref="aboutText">
        <transition name="fade" mode="out-in">
          <div v-if="aboutSlide === 0" key="slide0" class="text-justify space-y-8 w-full">
            <p class="text-base lg:text-lg font-gramatika text-darkGray mr-6 lg:mr-24">
              The program was conceived by the Government of Rwanda in 2022 through HangaPitchFest to explore the
              opportunity that exists for technology enabled startups to solve key issues in SRH to accelerate better
              outcomes.
            </p>
            <p class="text-base lg:text-lg font-gramatika text-darkGray mr-6 lg:mr-24">
              In 2023 we scouted, selected, and provided technical and business support to startups based in 11
              Sub-Saharan African countries working on innovative tech-enabled solutions that are addressing challenges in
              ways that break the mold in SRH. With the support of the Hanga SRH program, the 20 ventures maximized their
              impact at scale by raising over $1.4 million in funding, generating over $55,000 in revenue and impact over
              33,500 people.
            </p>
          </div>

          <div v-else-if="aboutSlide === 1" key="slide1" class="text-justify space-y-8 w-full">
            <p class="text-base lg:text-lg font-gramatika text-darkGray mr-6 lg:mr-24">
              Building on the success of cohort 1, In 2025, from over 450 applicants across Sub-Saharan Africa, Hanga's
              Cohort 2 selected 20 of the most promising, the most innovative, the most ready to change lives at scale.
              These are not just startups. They are proof that African-led, tech-driven SRH solutions exist, that they
              work, and they are building the future of SRH care, right now, on the continent, for the continent.
            </p>
          </div>

          <div v-else-if="aboutSlide === 2" key="slide2" class="text-justify space-y-6 w-full">
            <p class="text-base lg:text-lg font-gramatika text-darkGray mr-6 lg:mr-24">
              Spanning 11 countries, these 20 ventures are tackling the SRH challenges that remain most persistent and
              most urgent across the continent. Through their solutions, the ventures are:
            </p>
            <ul class="list-disc ml-6 text-base lg:text-lg font-gramatika text-darkGray mr-6 lg:mr-24 space-y-4">
              <li><span class="font-bold">Reimagining maternal health</span> by improving maternal health, beyond antenatal care, ensuring safe pregnancy, childbirth, and postpartum care</li>
              <li><span class="font-bold">Expanding access to safe abortion services</span> by removing barriers to safe abortion services and post-abortion care</li>
              <li>Meeting young people where they are by <span class="font-bold">targeting young people (10-24 years) with youth-friendly SRH information and services</span>.</li>
              <li><span class="font-bold">Tackling gender-based violence (GBV)</span> by preventing and responding to sexual and gender-based violence through innovative solutions</li>
              <li><span class="font-bold">Closing the contraception gap</span> by increasing access to contraception and comprehensive SRH education</li>
            </ul>
          </div>
        </transition>

        <!-- Slide dots -->
        <div class="flex justify-center w-full space-x-3 mt-8">
          <button v-for="i in 3" :key="i" @click="aboutSlide = i - 1"
            :class="aboutSlide === i - 1 ? 'bg-tangerine w-10' : 'bg-gray-300 w-4 hover:bg-gray-400'"
            class="h-4 rounded-full transition-all duration-300">
          </button>
        </div>
      </div>
    </div>

    <!-- ===== PHOTO PAIR ===== -->
    <div class="w-full mt-12">
      <div class="flex flex-col lg:flex-row lg:space-x-10 space-y-6 lg:space-y-0 p-4 lg:p-10">
        <div class="w-full h-[40vh] lg:h-[70vh]">
          <img src="../assets/new-2.jpg" class="w-full h-full object-cover" alt="" />
        </div>
        <div class="w-full h-[40vh] lg:h-[70vh]">
          <img src="../assets/new-3.jpg" class="w-full h-full object-cover" alt="" />
        </div>
      </div>
    </div>

    <!-- ===== FILTERED VENTURES ===== -->
    <div class="min-h-screen">
      <FilteredVentures />
    </div>

    <!-- ===== TOP 10 VENTURES + MAP ===== -->
    <div class="px-6 lg:px-24 mx-auto w-full mt-24 mb-24 max-w-[1200px]">
      <h2 class="font-rockinsoda text-navy text-5xl lg:text-6xl text-center">Hanga SRH Top 10 Ventures</h2>

      <div class="mt-8 text-base lg:text-lg font-gramatika text-darkGray max-w-4xl mx-auto text-center lg:text-left">
        <p>
          Meet the top 10 tech-driven ventures shortlisted from the initial 20 to progress to the acceleration phase.
          During the acceleration phase, the 10 ventures will receive:
        </p>
        <ul class="list-disc list-inside mt-6 space-y-2 lg:ml-4 text-left font-medium">
          <li>Up to $30,000 grant to scale their solutions</li>
          <li>Coaching by SRH and venture building experts</li>
          <li>Specialised workshops on relevant subject matter i.e. Revenue models</li>
          <li>Matchmaking with investors</li>
          <li>Access to a wider ecosystem within SRH, including funders</li>
        </ul>
      </div>

      <!-- Africa Map Image -->
      <div class="mt-12 mb-4">
        <img src="../assets/nsrh/hanga-map.jpeg" alt="Hanga SRH Africa Map" class="w-full h-auto" />
      </div>
    </div>

    <!-- ===== SECTION 5: SIGNALS OF IMPACT ===== -->
    <div class="px-6 lg:px-24 mx-auto w-full mt-24 mb-16 ">
      <div class="text-center mb-4">
        <p class="text-tangerine font-gramatika text-sm lg:text-base uppercase tracking-widest font-semibold">Cohort 2 Top 10 Ventures</p>
        <h2 class="font-rockinsoda text-navy text-4xl lg:text-6xl mt-2">Signals of Impact</h2>
      </div>
      <p class="text-base lg:text-lg font-gramatika text-darkGray max-w-3xl mx-auto text-center mb-14">
        Over the 11-month program period, from May 2025 to April 2026, the top 10 ventures progressed from early traction
        to meaningful scale, expanding their reach and strengthening frontline capabilities across their communities.
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div class="impact-card">
          <p class="impact-number">2.6×</p>
          <p class="impact-label">More People Reached</p>
          <p class="impact-desc">Beneficiaries grew from 89,150 to 232,590, enabling more communities to access essential SRH services.</p>
        </div>
        <div class="impact-card">
          <p class="impact-number">≈3.1×</p>
          <p class="impact-label">More Health Workers Trained</p>
          <p class="impact-desc">Trained 368 new health workers, strengthening frontline SRH capacity and expanding service delivery across clinics, health posts, and outreach programs.</p>
        </div>
        <div class="impact-card">
          <p class="impact-number">$1.28M</p>
          <p class="impact-label">In Cash & In-Kind Support Secured</p>
          <p class="impact-desc">Ventures secured $571,736 in cash and $712,829 in in-kind support, unlocking $4.28 for every $1 invested in them by the programme.</p>
        </div>
        <div class="impact-card">
          <p class="impact-number">≈2×</p>
          <p class="impact-label">More Jobs Created</p>
          <p class="impact-desc">Supported ventures hired 96 new employees, across both skilled and unskilled roles.</p>
        </div>
       
      </div>
    </div>

    <!-- ===== SECTION 6: HOW YOU CAN SUPPORT ===== -->
    <div class="relative mt-16 py-20 px-6 lg:px-24 bg-[#0e1e38] overflow-hidden">
      <div class="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-tangerine/10 pointer-events-none"></div>
      <div class="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-tangerine/10 pointer-events-none"></div>
      <div class="max-w-3xl mx-auto text-center relative z-10">
        <p class="text-tangerine font-gramatika text-sm lg:text-base uppercase tracking-widest font-semibold mb-3">Get Involved</p>
        <h2 class="font-rockinsoda text-white text-4xl lg:text-6xl leading-tight">How You Can Support</h2>
        <p class="mt-6 text-base lg:text-xl font-gramatika text-white/80 leading-relaxed">
          Whether you can offer funding, mentorship, technical expertise, or any form of in-kind support, your
          contribution matters. These ventures are already delivering results. What they need now are partners willing to
          back them for the long term.
        </p>
        <a href="https://bit.ly/SupportHangaSRH" target="_blank"
          class="inline-flex items-center space-x-3 mt-10 bg-tangerine hover:bg-tangerine/90 text-white font-gramatika font-semibold text-base lg:text-lg px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:scale-105">
          <span>Register Your Interest</span>
          <img src="../assets/Arrow 1.svg" alt="Arrow" class="w-5 h-5 brightness-0 invert" />
        </a>
        <p class="mt-6 font-gramatika text-white/55 text-sm">
          Once you fill in the form, we'll follow up to explore the best ways for you to get involved.
        </p>
      </div>
    </div>

    <scrollButton />
    <FooterPage />
  </div>
</template>

<script>
import NavInit from "@/components/NavInit.vue";
import FooterPage from "@/components/footerPage.vue";
import scrollButton from "@/components/scrollButton.vue";
import FilteredVentures from "@/components/FilteredVentures.vue";

export default {
  name: "HangaSrh",
  components: { NavInit, FooterPage, scrollButton, FilteredVentures },

  data() {
    return {
      aboutSlide: 0,
    };
  },

  mounted() {
    // Scroll to anchor if hash in URL
    const targetId = this.$route.hash.substring(1);
    const target = document.getElementById(targetId);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });

    // Intersection observer for fade-in animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("slide-up-visible", "zoom-in-visible", "fade-in-visible");
        }
      });
    });
    for (const key in this.$refs) {
      if (this.$refs[key]) observer.observe(this.$refs[key]);
    }
  },
};
</script>

<style>
/* ===== Page Layout ===== */
.page-wrapper {
  overflow-x: hidden;
  width: 100%;
}

.hero-text-section {
  margin-left: 0.5rem;
  margin-top: 8rem;
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
}
@media (min-width: 1024px) {
  .hero-text-section {
    margin-left: 2rem;
    flex-direction: row;
    margin-top: 8rem;
  }
}

.about-section {
  margin-left: 0.5rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 0.5rem;
  gap: 0;
}
@media (min-width: 1024px) {
  .about-section {
    margin-left: 2.5rem;
    flex-direction: row;
    margin-top: 4rem;
    gap: 1rem;
  }
}

/* ===== Impact Cards ===== */
.impact-card {
  /* background: linear-gradient(135deg, #f8f4ee 0%, #fff 100%); */
  border: 1px solid rgba(206, 96, 6, 0.2);
  border-radius: 2px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.impact-card:hover {
  transform: translateY(-4px);
}
.impact-number {
  font-family: 'Rockinsoda', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  color: #f59023;
  line-height: 1;
  margin-bottom: 0.5rem;
}
.impact-label {
  font-weight: 700;
  font-size: 1.7rem;
  color: #030C37;
  margin-bottom: 0.75rem;
}
.impact-desc {
  font-size: 1.2rem;
  color: #555;
  line-height: 1.6;
}

/* ===== Fade transition (about slider) ===== */
.fade-enter-active, .fade-leave-active { transition: opacity 0.5s ease; }
.fade-enter, .fade-leave-to           { opacity: 0; }

/* ===== Scroll-triggered animations ===== */
.zoom-in {
  transform: scale(0.95);
  opacity: 0;
  transition: transform 1s ease-in-out, opacity 0.9s ease-in-out;
}
.zoom-in-visible { transform: scale(1); opacity: 1; }

.fade-in { opacity: 0; transition: opacity 1s ease-in-out; }
.fade-in-visible { opacity: 1; }
</style>
